import { useBreakpointIndex } from '@theme-ui/match-media';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useMatch, useNavigate } from 'react-router';

import { SpeciesIdHuman } from 'utils/constants';
import { isMobile } from 'utils/responsive';

import { ViewPanel } from 'components/_controls/ViewPanel';

import diseaseActivitiesGlobalSelectors from 'diseaseActivitiesGlobal/diseaseActivitiesGlobal.selectors';
import { fetchDiseaseActivityDetails } from 'diseaseActivitiesInCountryLocations/diseaseActivitiesInCountryLocations.saga';
import diseaseActivityListSelectors from 'diseaseActivitiesInCountryLocations/diseaseActivityList.selectors';
import DiseaseActivityList from 'diseaseActivityPanels/components/DiseaseActivityList';
import { closePanel } from 'diseaseActivityPanels/diseaseActivityPanels.saga';
import diseaseActivityPanelsSelectors from 'diseaseActivityPanels/diseaseActivityPanels.selectors';
import { minimizePanel, PanelType } from 'diseaseActivityPanels/diseaseActivityPanels.slice';
import locationSelectors from 'locations/locations.selectors';

import diseaseActivitiesInLocationSelectors from '../diseaseActivitiesInLocation/diseaseActivitiesInLocation.selectors';
import { DiseaseActivitiesToolbar } from './components/DiseaseActivitiesToolbar';
import DiseaseSettingsIcon from './components/DiseaseSettingsIcon';
import DiseasesList from './components/DiseasesList';
import { GlobalDiseaseActivitiesToolbar } from './components/GlobalDiseaseActivitiesToolbar';
import GlobalDiseasesList from './components/GlobalDiseasesList';

const DiseasesContainer: React.FC = () => {
  const match = useMatch('/location/:geonameId/disease/:diseaseId');
  const navigate = useNavigate();

  useEffect(() => {
    if (match) navigate(`${match.pathname}/species/${SpeciesIdHuman}`);
  }, [match]);

  const isMobileDevice = isMobile(useBreakpointIndex());
  const isAnyLocationSelected = useSelector(diseaseActivityPanelsSelectors.isAnyLocationSelected);
  const isLoading = useSelector(diseaseActivitiesInLocationSelectors.isLoading);
  const isLoadingGlobal = useSelector(diseaseActivitiesGlobalSelectors.isLoading);
  const hasError = useSelector(diseaseActivitiesInLocationSelectors.hasError);
  const hasErrorGlobal = useSelector(diseaseActivitiesGlobalSelectors.hasError);

  const activePanel = useSelector(diseaseActivityPanelsSelectors.activePanel);
  const selectedLocationName = useSelector(locationSelectors.getSelectedLocationTitle);
  const diseaseName = useSelector(diseaseActivityListSelectors.getSubtitle);

  const { canClose, isMinimized, isOpen } = useSelector(
    diseaseActivityPanelsSelectors.getPanelVisibility(PanelType.DiseasesPanel)
  );

  const showPanel = !isMobileDevice ? isOpen : activePanel === PanelType.DiseasesPanel;

  const dispatch = useDispatch();
  const onMinimize = (flag: boolean): void => {
    dispatch(minimizePanel(PanelType.DiseasesPanel, flag));
  };

  return (
    <>
      {showPanel && (
        <>
          {isAnyLocationSelected ? (
            <ViewPanel
              title="Diseases"
              subtitle={diseaseName}
              subtitleMobile={selectedLocationName || 'Loading...'}
              isLoading={isLoading}
              hasError={hasError}
              canClose={canClose}
              onClose={() => dispatch(closePanel(PanelType.DiseasesPanel))}
              onRetryClick={() => dispatch(fetchDiseaseActivityDetails())}
              isMinimized={isMinimized}
              onMinimize={onMinimize}
              headerActions={<DiseaseSettingsIcon />}
              toolbar={<DiseaseActivitiesToolbar />}
            >
              <DiseasesList />
            </ViewPanel>
          ) : (
            <ViewPanel
              title="Diseases"
              subtitle={diseaseName}
              subtitleMobile={selectedLocationName || 'Loading...'}
              isLoading={isLoadingGlobal}
              hasError={hasErrorGlobal}
              canClose={canClose}
              onClose={() => dispatch(closePanel(PanelType.DiseasesPanel))}
              isMinimized={isMinimized}
              onMinimize={onMinimize}
              headerActions={<DiseaseSettingsIcon />}
              toolbar={<GlobalDiseaseActivitiesToolbar />}
            >
              <GlobalDiseasesList />
            </ViewPanel>
          )}
        </>
      )}
      <Routes>
        <Route path="disease/:diseaseId/species/:speciesId/*" element={<DiseaseActivityList />} />
      </Routes>
    </>
  );
};
export default DiseasesContainer;
